import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IMyImageQueryPaginated } from 'app/library/image-library/interfaces/my-image-paginated.query.interface';
import { ITenantImageQuery } from 'app/library/image-library/interfaces/tenant-image.query.interface';
import { MyImage } from 'app/library/image-library/models/my-image.model';
import { IModuleImageQuery } from 'app/library/module/interfaces/module-image.interface';
import { IModuleQuery } from 'app/library/module/interfaces/module.query.interface';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { FileCommand } from 'app/projects/core/src/lib/models/file.command';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { AiImageUploadCommand } from 'app/projects/user/src/lib/models/ai-image-upload.command';
import { MyImageUploadCommand } from 'app/projects/user/src/lib/models/my-image-upload.command';
import { TenantImageUploadCommand } from 'app/projects/user/src/lib/models/tenant-image-upload.command';
import { UnsplashImageUploadCommand } from 'app/projects/user/src/lib/models/unsplash-image-upload.command';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { MaxbrainImage } from '../../library/image-library/models/maxbrain-image.model';

export interface Pagination {
    numberOfPages: number;
    numberOfResults: number;
}

export interface ImageUploadProgress {
    fileName: string;
    percentDone: number;
    error?: HttpErrorResponse;
}

@Injectable()
export class MyImageApiService extends EntityService<ITenantImageQuery> {
    private _myImages = new BehaviorSubject<MyImage[]>([]);
    public myImages$ = this._myImages.asObservable();

    private _inProgressImages = new BehaviorSubject<ImageUploadProgress>(null);
    public inProgressImages$ = this._inProgressImages.asObservable();

    private _cancelImageUpload = new BehaviorSubject<string>(null);
    public cancelImageUpload$ = this._cancelImageUpload.asObservable();

    private uploadingCroppedImageSubject = new BehaviorSubject<boolean>(false);
    uploadingCroppedImage$ = this.uploadingCroppedImageSubject.asObservable();

    fetchingMyImages = false;
    cancelUploadingImages = false;
    myImagesPage = 1;

    private _tenantImagesPagination: Pagination = { numberOfPages: 0, numberOfResults: 0 };

    get tenantImagesPagination(): Pagination {
        return this._tenantImagesPagination;
    }
    set tenantImagesPagination(value: Pagination) {
        this._tenantImagesPagination = value;
    }

    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) environment: IEnvironment) {
        super(http, environment.apiUrl, 'cil');
    }

    setUploadingCroppedImage(state: boolean): void {
        this.uploadingCroppedImageSubject.next(state);
    }

    getMyImages(page: number): Observable<boolean> {
        this.myImagesPage = page;
        return this.http.get<IMyImageQueryPaginated>(`${this.apiUrl}/cil/my-images/images?page=${page}`).pipe(
            map((queries) => {
                this.tenantImagesPagination = {
                    numberOfPages: queries.pagination.number_of_pages,
                    numberOfResults: queries.pagination.number_of_results,
                };
                const myImages = queries.data.map((query) => new MyImage(query));
                this._myImages.next(myImages);
                return true;
            })
        );
    }

    getMyImagesFiltered(page: number, category: string): Observable<boolean> {
        this.myImagesPage = page;
        return this.http.get<IMyImageQueryPaginated>(`${this.apiUrl}/cil/my-images/images?page=${page}&filter=${category}`).pipe(
            map((queries) => {
                this.tenantImagesPagination = {
                    numberOfPages: queries.pagination.number_of_pages,
                    numberOfResults: queries.pagination.number_of_results,
                };
                const myImages = queries.data.map((query) => new MyImage(query));
                this._myImages.next(myImages);
                return true;
            })
        );
    }

    removeTenantImages(images: { id: string }[]): Promise<any> {
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: {
                images,
            },
        };
        return this.http.delete(`${this.apiUrl}/cil/tenant-library/images`, options).toPromise();
    }

    uploadCroppedImage(payload: File, selectedImage: MyImage): Observable<any> {
        let imageId = selectedImage.id;
        let data;
        let url: string = this.apiUrl;
        if (selectedImage.unsplash && selectedImage.myImagesSource === 'unsplash') {
            imageId = selectedImage.unsplash.unsplashId;
        }
        switch (selectedImage.myImagesSource) {
            case 'my_images':
                data = new MyImageUploadCommand(imageId, payload).formData;
                url = url + '/cil/my-images/upload';
                break;
            case 'unsplash':
                data = new UnsplashImageUploadCommand(imageId, payload).formData;
                url = url + '/cil/unsplash/upload';
                break;
            case 'tenant_library':
                data = new TenantImageUploadCommand(imageId, payload).formData;
                url = url + '/cil/tenant-library/upload';
                break;
            case 'open_ai':
                data = new AiImageUploadCommand(payload, null, imageId).formData;
                url = url + '/cil/ai-generated/upload';
                break;
            default:
                data = new MyImageUploadCommand(imageId, payload).formData;
                url = url + '/cil/my-images/upload';
                break;
        }
        return this.http.post<IModuleImageQuery>(url, data).pipe(map((query) => new MaxbrainImage(query)));
    }

    async uploadNewImage(payload: File): Promise<boolean> {
        const data = new FileCommand('file', new File([payload], '', { type: payload.type }), payload.name).formData;
        const url = `${this.apiUrl}/cil/my-images/upload`;

        try {
            const resp = await this._uploadFile(payload, data, url);
            if (!resp) {
                throw new Error();
            }
            this._inProgressImages.next({
                fileName: payload.name,
                percentDone: 100,
            });
            return true;
        } catch (e) {
            this._inProgressImages.next({
                fileName: payload.name,
                percentDone: 0,
                error: e,
            });
            return false;
        }
    }

    private _uploadFile(file: File, data: FormData, url: string): Promise<MaxbrainImage> {
        return this.http
            .request<IModuleImageQuery>(
                new HttpRequest('POST', url, data, {
                    reportProgress: true,
                })
            )
            .pipe(
                takeUntil(
                    this.cancelImageUpload$.pipe(
                        filter((fileName) => fileName === file.name),
                        take(1)
                    )
                ),
                tap((e) => {
                    if (e.type === HttpEventType.UploadProgress) {
                        const percentDone = Math.round((100 * e.loaded) / e.total);
                        if (percentDone < 100) {
                            this._inProgressImages.next({
                                fileName: file.name,
                                percentDone,
                            });
                        }
                    }
                }),
                filter((e) => e instanceof HttpResponse),
                map((e: HttpResponse<IModuleImageQuery>) => new MaxbrainImage(e.body))
            )
            .toPromise();
    }

    cancelUploadingImage(fileName: string): void {
        this._cancelImageUpload.next(fileName);
    }

    addModuleImage(moduleImageId: string, moduleId: string, moduleType: string): Observable<MaxbrainImage> {
        const data = { image: { id: moduleImageId } };
        if (moduleType === 'e_learning') {
            return this.http.put<IModuleQuery>(`${this.apiUrl}/e-learning/modules/${moduleId}`, data).pipe(map((query) => new MaxbrainImage(query.image)));
        } else if (moduleType === 'basic') {
            return this.http.put<IModuleQuery>(`${this.apiUrl}/modules/${moduleId}`, data).pipe(map((query) => new MaxbrainImage(query.image)));
        }
    }

    deleteModuleImage(imageId: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/${this.entities}/my-images/images/${imageId}`);
    }
}
